import { useRef } from 'react';

/**
 * Horizontal drag handle for resizing a vertically-split panel
 * (Live Map's vehicle/people list above + docked telemetry below).
 *
 * Implementation note — stale-closure trap: when React re-renders
 * the parent on every mousemove (because the value prop changed),
 * the window's mousemove listener that was added in onMouseDown
 * still points at the ORIGINAL handler closure. If that closure
 * reads the current value via props, it stays frozen at the
 * mousedown-time snapshot and the handle appears to move once then
 * freeze. Fix: capture startY + startValue at mousedown via refs,
 * then on each mousemove compute the absolute new value from the
 * cumulative cursor delta — no dependency on stale prop values.
 *
 * Props:
 *  - value      : current height in px (single source of truth, parent-owned)
 *  - onChange   : (nextValue: number) => void
 *  - direction  : 'up-grows' | 'down-grows' (default 'up-grows')
 *      'up-grows'   = dragging up makes the controlled value larger
 *      'down-grows' = dragging down makes it larger
 *  - ariaLabel  : optional
 */
export default function DockResizeHandle({
  value,
  onChange,
  direction = 'up-grows',
  ariaLabel = 'Resize telemetry',
}) {
  const dragRef = useRef({ startY: 0, startValue: 0 });

  const handleDown = (e) => {
    e.preventDefault();
    dragRef.current.startY = e.clientY;
    dragRef.current.startValue = value;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev) => {
      const dy = ev.clientY - dragRef.current.startY;
      // up-grows: cursor up (negative dy) → larger value
      // down-grows: cursor down (positive dy) → larger value
      const signed = direction === 'up-grows' ? -dy : dy;
      onChange(dragRef.current.startValue + signed);
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div
      onMouseDown={handleDown}
      role="separator"
      aria-orientation="horizontal"
      aria-label={ariaLabel}
      className="flex-shrink-0 flex items-center justify-center group"
      style={{
        height: 6,
        cursor: 'row-resize',
        background: 'rgba(15,23,42,0.04)',
        borderTop: '1px solid var(--glass-border)',
        borderBottom: '1px solid var(--glass-border)',
      }}
    >
      <span
        className="flex items-center gap-0.5 opacity-40 group-hover:opacity-80 transition-opacity"
        aria-hidden="true"
      >
        <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#64748b' }} />
        <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#64748b' }} />
        <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#64748b' }} />
      </span>
    </div>
  );
}
